import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, LogOut, Warehouse } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import NotificationBell from '@/components/owner/NotificationBell';
import { useIncompleteDeliveries } from '@/hooks/useIncompleteDeliveries';
import { cn } from '@/lib/utils';

// Path yang punya badge notif "info pengiriman belum lengkap"
const DOCS_PATHS = new Set(['/admin/documents', '/kasir/documents']);

export function AppShell({ nav, title, children, showBell = false }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Fetch count untuk role yang bisa cetak dokumen (owner/admin/kasir).
  // Owner tidak punya menu /admin/documents jadi tidak akan tampil badge — hook tetap jalan aman.
  const canSeeDocs = user && ['owner', 'admin', 'kasir'].includes(user.role);
  const { count: incompleteCount } = useIncompleteDeliveries(24, { silent: !canSeeDocs });

  const NavContent = () => (
    <nav className="flex flex-col gap-1 p-3">
      {nav.map((n) => {
        const active = location.pathname === n.path || (n.path !== '/' && location.pathname.startsWith(n.path + '/'));
        const Icon = n.icon;
        const showBadge = DOCS_PATHS.has(n.path) && incompleteCount > 0;
        return (
          <Link
            key={n.path}
            to={n.path}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.path.replace(/\//g, '-')}`}
            className={cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors',
              active ? 'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-accent'
            )}
          >
            <Icon className="h-4 w-4" />
            <span className="flex-1">{n.label}</span>
            {showBadge && (
              <Badge
                variant="destructive"
                className={cn(
                  'h-5 min-w-5 px-1.5 text-[10px] tabular-nums',
                  active ? 'bg-background/25 text-primary-foreground border-primary-foreground/30' : 'bg-rose-500 text-white'
                )}
                data-testid={`nav-badge-incomplete-${n.path.replace(/\//g, '-')}`}
              >
                {incompleteCount > 99 ? '99+' : incompleteCount}
              </Badge>
            )}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r bg-card">
        <div className="h-16 flex items-center gap-3 px-4 border-b">
          <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center text-primary-foreground">
            <Warehouse className="h-5 w-5" />
          </div>
          <div>
            <div className="font-heading font-semibold">ERP Zainusa Pos</div>
            <div className="text-[11px] text-muted-foreground capitalize">{user?.role} Panel</div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto"><NavContent /></div>
        <div className="p-3 border-t">
          <div className="text-xs text-muted-foreground mb-2">Login sebagai</div>
          <div className="text-sm font-medium truncate">{user?.full_name}</div>
          <div className="text-xs text-muted-foreground truncate">@{user?.username}</div>
          <Button data-testid="logout-button" variant="outline" size="sm" className="w-full mt-3" onClick={logout}>
            <LogOut className="h-4 w-4 mr-2" /> Keluar
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 flex items-center gap-3 px-4 border-b bg-card sticky top-0 z-30">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <div className="h-16 flex items-center gap-3 px-4 border-b">
                <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center text-primary-foreground">
                  <Warehouse className="h-5 w-5" />
                </div>
                <div className="font-heading font-semibold">ERP Zainusa Pos</div>
              </div>
              <NavContent />
              <div className="p-3 border-t">
                <Button variant="outline" size="sm" className="w-full" onClick={logout}>
                  <LogOut className="h-4 w-4 mr-2" /> Keluar
                </Button>
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex-1 min-w-0">
            <div className="font-heading text-lg font-semibold truncate">{title}</div>
          </div>

          {showBell && <NotificationBell />}
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-full">
          {children}
        </main>
      </div>
    </div>
  );
}

export default AppShell;
